import React, { useCallback, useMemo, useState } from 'react';
import { FieldFilter } from '@components/FieldFilter';
import { filterOperatorOptions } from '@components/filters/utils';
import { MultiSelectTags } from '@components/Select/MultiSelectTags';
import { OptionObject } from '@components/Select/types';
import { ArrowDown } from '@fabric-ui/icons';
import { ellipsis, flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button, Theme, useTheme } from '@mui/material';
import { getIfValidStringifiedObject } from '@utils/utils';
import { useAvContext } from '../../context/AvContextProvider';
import { Filter, StringConditionType } from '../../types/filter.types';

interface FieldFilterBoxSelectProps {
  fieldName?: string | null;
  size: 'xSmall' | 'small';
  value: Filter;
  onChange: (filter: Filter) => void;
}

const rawExpressionToFilter = (fieldName, exp): Filter => {
  const op = exp.and ? 'and' : exp.or ? 'or' : undefined;

  if (!op) {
    return exp.expression;
  }

  const { operator, value } = exp[op][0];
  return {
    expression: {
      fieldName,
      stringCondition: { [operator]: [StringConditionType.EMPTY, StringConditionType.NOT_EMPTY].includes(operator) ? {} : value },
    },
  };
};

const filterToRawExpression = (filter: Filter) => {
  const [operator, value] = filter.expression?.stringCondition ? Object.entries(filter.expression?.stringCondition)[0] : [];

  if (!operator || !value) {
    return null;
  }

  return {
    and: [{ operator, value }],
  };
};

const expressionStyle = { ...flex.justifyStartCenter, width: 'calc(100% - 28px)', position: 'absolute', left: 0, top: 0, bottom: 0 };

const operatorLabelStyle = ({ palette }: Theme) => ({
  ...flex.center,
  height: '100%',
  backgroundColor: palette.colors.neutrals[200],
  color: palette.colors.neutrals[800],
  fontWeight: 600,
  mr: '8px',
  px: '10px',
});

const valueLabelStyle = ({ palette }) => ({
  ...ellipsis,
  color: palette.colors.neutrals[800],
});

const ExpressionContent = ({ rawExpression }) => {
  const { palette } = useTheme();

  const operatorColors = {
    and: palette.colors.primary[500],
    or: palette.colors.complementary[600],
  };

  const operator = rawExpression.and ? 'and' : rawExpression.or ? 'or' : undefined;

  if (!operator) {
    return null;
  }

  if (rawExpression[operator].length === 1) {
    const { operator: op, value } = rawExpression[operator][0];
    return (
      <Box sx={expressionStyle}>
        <Box sx={operatorLabelStyle}>{filterOperatorOptions.string.find(({ value }) => value === op)?.title}</Box>
        {typeof value === 'string' ? <Box sx={valueLabelStyle}>{value}</Box> : null}
      </Box>
    );
  }

  return (
    <Box sx={{ ...flex.row }}>
      {rawExpression[operator].map(({ value, operator: op }, i) => (
        <Box sx={{ ...flex.row }} key={value}>
          {i > 0 && <span style={{ margin: '0 4px', fontWeight: 600, color: operatorColors[operator!] }}>{operator.toUpperCase()}</span>}
          <Box sx={{ ...flex.row, gap: '4px', color: palette.colors.neutrals[800] }}>
            <Box sx={{ fontWeight: 600 }}>{filterOperatorOptions.string.find(({ value }) => value === op)?.title}</Box>
            {typeof value === 'string' ? <div>&quot;{value}&quot;</div> : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const fieldFilterBoxSelectStyle =
  (size: 'xSmall' | 'small', isOpen: boolean) =>
  ({ palette }: Theme) => ({
    ...flex.row,
    ...ellipsis,
    height: size === 'xSmall' ? '24px' : '32px',
    justifyContent: 'start',
    border: `1px solid ${isOpen ? palette.colors.primary[500] : palette.colors.neutrals[400]}`,
    borderRadius: size === 'xSmall' ? '8px' : '10px',
    fontWeight: 400,
    fontSize: size === 'xSmall' ? '12px' : '14px',
    color: palette.colors.neutrals[600],
    width: size === 'xSmall' ? '182px' : '406px',
    py: size === 'xSmall' ? '2px' : '4px',
    pl: '8px',
    gap: '4px',
    ...(size === 'xSmall' ? {} : {}),
    '& > svg': {
      ...iconSize(18),
      position: 'absolute',
      right: '10px',
      color: palette.colors.neutrals[600],
    },
    '&:hover': {
      color: palette.colors.neutrals[600],
      borderColor: isOpen ? palette.colors.primary[500] : palette.colors.neutrals[800],
    },
    '& .MuiChip-root': {
      py: 0,
      pr: '6px',
      backgroundColor: palette.colors.primary[150],
      ...(size === 'xSmall'
        ? {
            pl: '4px',
            gap: '6px',
            '& .MuiChip-label': {
              fontSize: '12px',
              mr: 0,
            },
          }
        : {
            pl: '10px',
          }),
    },
  });

export const FieldFilterBoxSelect = ({ value, onChange, fieldName, size }: FieldFilterBoxSelectProps) => {
  const {
    accountEntities: { fieldMap },
  } = useAvContext();

  const activeProjName = useMemo(() => (fieldName ? fieldMap[fieldName].mainProjId.name : undefined), [fieldName, fieldMap]);
  const [isOpen, setIsOpen] = useState(false);
  const rawExpression = useMemo(() => filterToRawExpression(value), [value]);

  const filters = useMemo(
    () => value.expression?.stringCondition?.in?.values ?? (rawExpression ? [encodeURIComponent(JSON.stringify(rawExpression))] : []),
    [value, rawExpression]
  );

  const onFilterChange = (field: string | string[], newValue: string[]) => {
    const fieldName = Array.isArray(field) ? field[0] : field;
    const rawExpression = getIfValidStringifiedObject(newValue[0]);
    if (!rawExpression) {
      onChange({
        expression: { fieldName, stringCondition: { in: { values: newValue } } },
      });
    } else {
      onChange(rawExpressionToFilter(fieldName, rawExpression) ?? {});
    }
  };

  const selectedFilterOptions: OptionObject<string>[] = useMemo(() => filters.map(f => ({ label: f || 'Empty', value: f })), [filters]);

  const handleTagDelete = useCallback(
    (index: number) => {
      const newFilters = filters.filter((_, i) => i !== index);
      onFilterChange(fieldName || '', newFilters);
    },
    [filters, fieldName, onFilterChange]
  );

  const getTagProps = useCallback(
    ({ index }: { index: number }) => ({
      onDelete: () => handleTagDelete(index),
    }),
    [handleTagDelete]
  );

  const trigger = useMemo(
    () => (
      <Button sx={fieldFilterBoxSelectStyle(size, isOpen)}>
        {rawExpression && rawExpression?.and?.[0]?.operator !== 'in' ? (
          <ExpressionContent rawExpression={rawExpression} />
        ) : selectedFilterOptions.length > 0 ? (
          <MultiSelectTags
            tags={selectedFilterOptions}
            getTagProps={getTagProps}
            size={size}
            getLabelFunc={({ label }: any) => label}
            getValueFunc={({ value }: any) => value}
          />
        ) : (
          'Select value'
        )}
        <ArrowDown style={isOpen ? { transform: 'rotate(180deg)' } : {}} />
      </Button>
    ),
    [size, value, rawExpression, selectedFilterOptions, getTagProps]
  );

  return (
    <FieldFilter
      filters={{ [fieldName!]: filters }}
      disabled={!fieldName}
      field={fieldName ?? ''}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isEnabled={!!fieldName}
      onChange={onFilterChange}
      size={size}
      activeProjName={activeProjName}
      trigger={trigger}
      isMultipleCondition={false}
      disablePortalInOperatorSelect={false}
    />
  );
};
